import type { Agent, CustomerRecord, Studio, StudioId } from '@/domain/types';
import type { RedKind } from '@/domain/sequenceFlow';

/**
 * Nurture (JustCall re-engagement) — the SMS-only re-engagement channel.
 *
 * Segments each get their own editable copy; a send is automated (mock JustCall
 * seam this phase). When a nurtured contact replies or calls back, it routes to
 * the customer's studio agent and surfaces as an Alert carrying a "why" summary.
 * This module holds the pure pieces (segments, templates, rendering, routing,
 * summary); the store-touching seam (send + inbound) lives in nurtureSeam.ts.
 */

export type NurtureSegment = 'historic' | 'light-red' | 'dark-red';

export const NURTURE_SEGMENTS: readonly NurtureSegment[] = ['historic', 'light-red', 'dark-red'];

/** An outbound nurture SMS touch (mock JustCall send). */
export interface NurtureTouch {
  id: string;
  customerId: string;
  studioId: StudioId;
  segment: NurtureSegment;
  body: string; // the rendered copy that went out
  sentAt: string;
}

/**
 * An inbound reply / call-back on a nurture touch, routed to the studio agent.
 * Surfaces on that agent's Alerts tab with `summary` (the "why"), until handled.
 */
export interface NurtureInbound {
  id: string;
  customerId: string;
  studioId: StudioId;
  agentId: string | null;
  segment: NurtureSegment;
  kind: 'reply' | 'callback';
  text?: string;
  summary: string;
  touchBody?: string; // the copy that went out, for context
  at: string;
  handledAt?: string;
}

export interface NurtureTemplate {
  segment: NurtureSegment;
  label: string;
  /** Body with {name} {artist} {studio} {piece} variables. */
  body: string;
}

/** Editable defaults — the operator can override these (persisted per studio). */
export const DEFAULT_NURTURE_TEMPLATES: Record<NurtureSegment, NurtureTemplate> = {
  historic: {
    segment: 'historic',
    label: 'Historic · lapsed re-engagement',
    body: "Hey {name}, it's {studio} — it's been a while! Keen to get you back in with {artist}? Reply here and we'll sort a time.",
  },
  'light-red': {
    segment: 'light-red',
    label: 'Light red · no answer',
    body: "Hi {name}, we tried reaching you about your tattoo — reply here and we'll lock in a time with {artist}.",
  },
  'dark-red': {
    segment: 'dark-red',
    label: 'Dark red · previously declined',
    body: "No worries {name} — whenever you're ready to book with {artist}, just reply and we'll pick up where we left off.",
  },
};

/** Which nurture segment a red lead recycles into (null = not a nurtured red). */
export function segmentForRedKind(redKind: RedKind): NurtureSegment | null {
  if (redKind === 'never-reached') return 'light-red'; // no answer after the ladder
  if (redKind === 'said-no') return 'dark-red'; // declined
  return null;
}

/** Fill {name} {artist} {studio} {piece} in a template body. */
export function renderNurtureCopy(
  body: string,
  vars: { name?: string; artist?: string; studio?: string; piece?: string },
): string {
  return body
    .replace(/\{name\}/g, vars.name?.trim() || 'there')
    .replace(/\{artist\}/g, vars.artist?.trim() || 'your artist')
    .replace(/\{studio\}/g, vars.studio?.trim() || 'the studio')
    .replace(/\{piece\}/g, vars.piece?.trim() || 'your piece');
}

/**
 * The customer's studio's agent (one agent per studio). Prefers the agent whose
 * primary studio is this one, else the studio's first assigned agent.
 */
export function agentForStudio(
  studioId: StudioId,
  data: { studios: Record<string, Studio>; agents: Record<string, Agent> },
): string | null {
  // The studio's own bookings agent first: a studio manager or a seated person
  // (L4b's Floor seat) can share the primary studio, and must not take the alert.
  const primaries = Object.values(data.agents).filter((a) => a.primaryStudio === studioId);
  const byPrimary = primaries.find((a) => a.role === 'bookings-agent') ?? primaries[0];
  if (byPrimary) return byPrimary.id;
  return data.studios[studioId]?.assignedAgentIds?.[0] ?? null;
}

const SEGMENT_REASON: Record<NurtureSegment, string> = {
  historic: 'Lapsed client · re-engagement',
  'light-red': 'Light red · no pickup on the booking chase',
  'dark-red': 'Dark red · previously declined',
};

/** The "why they're reaching out" line the agent sees on the Alert. */
export function nurtureSummary(args: {
  segment: NurtureSegment;
  kind: 'reply' | 'callback';
  text?: string;
}): string {
  const trigger =
    args.kind === 'reply'
      ? `Replied${args.text ? `: "${args.text}"` : ''}`
      : 'Called back';
  return `${SEGMENT_REASON[args.segment]} — ${trigger}`;
}

/** Convenience: derive the copy variables for a customer + studio + artist. */
export function nurtureVarsFor(args: {
  customer: CustomerRecord;
  studioName?: string;
  artistName?: string;
}): { name: string; studio?: string; artist?: string } {
  return {
    name: args.customer.firstName,
    studio: args.studioName,
    artist: args.artistName,
  };
}
